import React, { useEffect, useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import ProductGroup from '../../../../../components/common/ProductGroup';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const FaultCustomerDefinitionDialog = ({ visible, onHide, onSubmit, initialData, isEdit, supportedLanguages = [], loading }) => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        id: null,
        startDate: null,
        endDate: null,
        faultType: 1,
        productGroup: '',
        isActive: true,
        translations: [
            { text: '', languageId: 1 },
            { text: '', languageId: 2 }
        ]
    });

    useEffect(() => {
        if (isEdit && initialData && supportedLanguages.length > 0) {
            const fullTranslations = supportedLanguages.map((lang) => {
                const existing = initialData.translations?.find((t) => t.languageId == lang.id);
                return {
                    languageId: lang.id,
                    text: existing?.text || ''
                };
            });

            setFormData((prev) => ({
                ...prev,
                ...initialData,
                startDate: new Date(initialData.startDate),
                endDate: initialData.endDate && !isNaN(new Date(initialData.endDate)) && initialData.endDate !== '0001-01-01T00:00:00' ? new Date(initialData.endDate) : null,
                translations: fullTranslations
            }));
        } else if (!isEdit && supportedLanguages.length > 0) {
            const emptyTranslations = supportedLanguages.map((lang) => ({
                languageId: lang.id,
                text: ''
            }));

            setFormData({
                id: null,
                startDate: null,
                endDate: null,
                faultType: 1,
                productGroup: '',
                isActive: true,
                translations: emptyTranslations
            });
            setErrors({});
        }
    }, [isEdit, initialData, visible, supportedLanguages]);

    const getFlagClassNameForDialog = (languageCode) => {
        if (!languageCode) return 'fi fi-xx';
        const codeToFlagSuffixMap = {
            en: 'us',
            tr: 'tr',
            de: 'de',
            fr: 'fr',
            it: 'it',
            es: 'es',
            ro: 'ro'
        };
        const lowerCaseCode = languageCode.toLowerCase();
        const flagSuffix = codeToFlagSuffixMap[lowerCaseCode];
        return flagSuffix ? `fi fi-${flagSuffix}` : `fi fi-${lowerCaseCode}`;
    };

    const resetForm = () => {
        setFormData({
            id: null,
            startDate: null,
            endDate: null,
            faultType: 1,
            productGroup: '',
            isActive: true,
            translations: [
                { text: '', languageId: 1 },
                { text: '', languageId: 2 }
            ]
        });
        setErrors({});
    };
    const [errors, setErrors] = useState({});

    const handleSubmit = async () => {
        const newErrors = {};
        if (!formData.faultType) newErrors.faultType = true;
        if (!getTranslationText(2).trim()) newErrors.trText = true;
        if (!getTranslationText(1).trim()) newErrors.enText = true;
        if (!formData.startDate) newErrors.startDate = true;
        if (!formData.productGroup) newErrors.productGroup = true;
        if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
            newErrors.dateRange = t('warranty.faultCustomerDefinitions.validation.dateRange');
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const filteredTranslations = formData.translations.filter((t) => t.text.trim() !== '');
        const submitData = {
            ...formData,
            startDate: formData.startDate?.toISOString(),
            endDate: formData.endDate?.toISOString(),
            isActive: formData.isActive ? 1 : 0,
            translations: filteredTranslations
        };
        const success = await onSubmit(submitData);
        if (success) {
            onHide();
            resetForm();
        }
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleTranslationChange = (languageId, value) => {
        setFormData((prev) => ({
            ...prev,
            translations: prev.translations.map((t) => (t.languageId == languageId ? { ...t, text: value } : t))
        }));
    };

    const getTranslationText = (languageId) => {
        return formData.translations.find((t) => t.languageId === languageId)?.text || '';
    };

    const sortedLanguages = useMemo(() => {
        if (!supportedLanguages || supportedLanguages.length === 0) return [];
        return [...supportedLanguages].sort((a, b) => {
            if (a.code.toLowerCase() === 'en') return -1;
            if (b.code.toLowerCase() === 'en') return 1;
            if (a.code.toLowerCase() === 'tr') return -1;
            if (b.code.toLowerCase() === 'tr') return 1;
            return (a.name || a.code).localeCompare(b.name || b.code);
        });
    }, [supportedLanguages]);

    const faultTypeOptions = [
        { label: t('warranty.faultCustomerDefinitions.dialog.faultType1'), value: 1 },
        { label: t('warranty.faultCustomerDefinitions.dialog.faultType2'), value: 2 }
    ];

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={t('warranty.faultCustomerDefinitions.dialog.cancel')} icon="pi pi-times" onClick={onHide} outlined disabled={loading} />
            <Button label={t('warranty.faultCustomerDefinitions.dialog.save')} icon="pi pi-check" onClick={handleSubmit} loading={loading} />
        </div>
    );

    return (
        <Dialog visible={visible} style={{ width: '650px' }} header={isEdit ? t('warranty.faultCustomerDefinitions.editTitle') : t('warranty.faultCustomerDefinitions.createTitle')} modal className="p-fluid" footer={footer} onHide={onHide}>
            <div className="grid">
                <div className="col-12">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{t('warranty.faultCustomerDefinitions.dialog.faultType')} *</label>
                        <div className="flex gap-3">
                            {faultTypeOptions.map((opt) => (
                                <div className="flex align-items-center" key={opt.value}>
                                    <RadioButton inputId={`faultType${opt.value}`} name="faultType" value={opt.value} onChange={(e) => handleInputChange('faultType', e.value)} checked={formData.faultType === opt.value} />
                                    <label htmlFor={`faultType${opt.value}`} className="ml-2">
                                        {opt.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {errors.faultType && <small className="p-error">{t('warranty.faultCustomerDefinitions.validation.faultTypeRequired')}</small>}
                    </div>
                </div>

                {sortedLanguages.map((lang, index) => {
                    const langCode = lang.code?.toLowerCase() || '';
                    const translationEntry = formData.translations.find((item) => item.languageId == lang.id);
                    const currentText = translationEntry?.text || '';
                    const isNameRequired = ['tr', 'en'].includes(langCode);

                    return (
                        <div key={lang.id || lang.code} className="col-12 md:col-6">
                            <div className="field mb-4">
                                <label className="font-bold block mb-2">
                                    <span className={getFlagClassNameForDialog(langCode)} title={lang.name || langCode.toUpperCase()} />
                                    <span className="ml-2">
                                        {lang.name || langCode.toUpperCase()}
                                        {isNameRequired && <span className="text-red-500">*</span>}
                                    </span>
                                </label>
                                <InputTextarea value={currentText} onChange={(e) => handleTranslationChange(lang.id, e.target.value)} rows={1} autoResize />
                                {langCode === 'tr' && errors.trText && <small className="p-error">{t('warranty.faultCustomerDefinitions.validation.languageTextRequired')}</small>}
                                {langCode === 'en' && errors.enText && <small className="p-error">{t('warranty.faultCustomerDefinitions.validation.languageTextRequired')}</small>}
                            </div>
                        </div>
                    );
                })}

                <div className="col-12 md:col-6">
                    <div className="field mb-4">
                        <label htmlFor="startDate" className="font-bold block mb-2">
                            {t('warranty.faultCustomerDefinitions.dialog.startDate')}
                        </label>
                        <Calendar id="startDate" value={formData.startDate} onChange={(e) => handleInputChange('startDate', e.value)} dateFormat="dd/mm/yy" showIcon className={errors.startDate ? 'p-invalid' : ''} />
                        {errors.startDate && <small className="p-error">{t('warranty.faultCustomerDefinitions.validation.startDateRequired')}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field mb-4">
                        <label htmlFor="endDate" className="font-bold block mb-2">
                            {t('warranty.faultCustomerDefinitions.dialog.endDate')}
                        </label>
                        <Calendar id="endDate" value={formData.endDate} onChange={(e) => handleInputChange('endDate', e.value)} dateFormat="dd/mm/yy" showIcon minDate={formData.startDate} />
                        {errors.dateRange && <small className="p-error">{errors.dateRange}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field mb-4">
                        <label htmlFor="productGroup" className="font-bold block mb-2">
                            {t('warranty.faultCustomerDefinitions.dialog.productGroup.label')}
                        </label>
                        <ProductGroup
                            value={formData.productGroup}
                            onChange={(value) => handleInputChange('productGroup', value)}
                            className={errors.productGroup ? 'p-invalid' : ''}
                            placeholder={t('warranty.faultCustomerDefinitions.dialog.productGroup.placeholder')}
                        />
                        {errors.productGroup && <small className="p-error">{t('warranty.faultCustomerDefinitions.validation.productGroupRequired')}</small>}
                    </div>
                </div>

                <div className="col-12">
                    <div className="field-checkbox mb-4">
                        <Checkbox inputId="isActive" checked={!!formData.isActive} onChange={(e) => handleInputChange('isActive', e.checked)} />
                        <label htmlFor="isActive" className="ml-2">
                            {t('warranty.faultCustomerDefinitions.dialog.active')}
                        </label>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default FaultCustomerDefinitionDialog;
